import React, { useEffect } from 'react';
import { Box, Typography, Table, TableHead, TableBody, TableRow, TableCell } from '@mui/material';

function usePrintStyles() {
  useEffect(() => {
    const el = document.createElement('style');
    el.id = 'amc-print-styles';
    el.innerHTML = '@media screen { .print-layout { display: none; } }';
    document.head.appendChild(el);
    return () => { document.getElementById('amc-print-styles')?.remove(); };
  }, []);
}

export const PRINT_WINDOW_CSS = `
* { box-sizing: border-box; margin: 0; padding: 0; }
body { font-family: Arial, sans-serif; font-size: 13px; }
.print-page { page-break-after: always; break-after: page; padding: 20px 24px; }
.print-page:last-of-type { page-break-after: avoid; break-after: avoid; }
h5 { font-size: 18px; font-weight: bold; margin-bottom: 12px; }
h6 { font-size: 13px; font-weight: 600; margin-top: 16px; margin-bottom: 4px; }
p { font-size: 12px; margin-bottom: 4px; }
table { width: 100%; border-collapse: collapse; margin-top: 4px; }
thead th { background: #f0f0f0; font-weight: bold; border: 1px solid #bbb; padding: 4px 8px; font-size: 12px; text-align: left; }
tbody td { border: 1px solid #ccc; padding: 3px 8px; font-size: 12px; }
.top-by-category { margin-top: 20px; padding-top: 12px; border-top: 1px solid #ddd; }
.top-by-category h6 { margin-top: 0; }
strong { font-weight: 600; }
`;

// Fixed category display order — match against categoriesMap values (case-insensitive)
const CATEGORY_ORDER = ['General', 'Military', 'MRO', 'Commercial', 'School'];

// Tie-safe top-N: include all rows where place <= n
function topN(arr, placeKey, n) {
  const withPlace = arr.filter(r => Number.isFinite(r[placeKey]) && r[placeKey] > 0);
  withPlace.sort((a, b) => a[placeKey] - b[placeKey]);
  return withPlace.filter(r => r[placeKey] <= n);
}

// Sort event UIDs by event.number ascending
function sortedEventUids(eventsScores) {
  return Object.keys(eventsScores).sort((a, b) => {
    const an = Number(eventsScores[a]?.event?.number) || Infinity;
    const bn = Number(eventsScores[b]?.event?.number) || Infinity;
    return an - bn;
  });
}

// Return category UIDs ordered by CATEGORY_ORDER (unmatched names appended at end)
function orderedCategoryUids(categoriesMap) {
  const lower = CATEGORY_ORDER.map(n => n.toLowerCase());
  const matched = lower
    .map(name => Object.keys(categoriesMap).find(uid => (categoriesMap[uid] || '').toLowerCase() === name))
    .filter(Boolean);
  const rest = Object.keys(categoriesMap).filter(uid => !matched.includes(uid));
  return [...matched, ...rest];
}

export default function PrintLayout({ eventsScores, adjustedTeamsMap, categoriesMap, divisionsMap, section }) {
  usePrintStyles();
  // section: 'all' | 'events' | 'division' | 'category' | 'overall'
  const show = (s) => section === 'all' || section === s;

  return (
    <div className="print-layout">
        {show('division') && <ForeignDivisionPage adjustedTeamsMap={adjustedTeamsMap} divisionsMap={divisionsMap} categoriesMap={categoriesMap} />}
        {show('events') && <EventPages eventsScores={eventsScores} categoriesMap={categoriesMap} />}
        {show('division') && <DivisionPages adjustedTeamsMap={adjustedTeamsMap} divisionsMap={divisionsMap} categoriesMap={categoriesMap} />}
        {show('category') && <CategoryPages adjustedTeamsMap={adjustedTeamsMap} categoriesMap={categoriesMap} />}
        {show('overall') && <OverallPage adjustedTeamsMap={adjustedTeamsMap} categoriesMap={categoriesMap} divisionsMap={divisionsMap} />}
    </div>
  );
}

function PrintPage({ children }) {
  return (
    <div className="print-page" style={{ padding: '16px' }}>
      {children}
    </div>
  );
}

function ResultTable({ rows, columns }) {
  // rows: array of objects; columns: [{ key, label }]
  return (
    <Table size="small" sx={{ mt: 1, '& th': { fontWeight: 700 } }}>
      <TableHead>
        <TableRow>
          {columns.map(c => <TableCell key={c.key}>{c.label}</TableCell>)}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row, i) => (
          <TableRow key={row.teamNumber != null ? row.teamNumber : i}>
            {columns.map(c => <TableCell key={c.key}>{row[c.key] ?? ''}</TableCell>)}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function EventPages({ eventsScores, categoriesMap }) {
  const eventUids = sortedEventUids(eventsScores);
  const catUids = orderedCategoryUids(categoriesMap || {});

  return eventUids.map((evUid) => {
    const ev = eventsScores[evUid];
    if (!ev) return null;
    const scores = (ev.scores || []).filter(s => Number(s.finalScore) > 0);

    const rawAssoc = ev.event?.associatedTeams || [];
    const assocArr = Array.isArray(rawAssoc) ? rawAssoc : Object.values(rawAssoc || {});
    const assocTeams = assocArr.map(a => typeof a === 'string' ? a : a?.uid).filter(Boolean);
    const top8rows = topN(scores, 'overallPlace', 8);
    const placeCounts = top8rows.reduce((acc, s) => { acc[s.overallPlace] = (acc[s.overallPlace] || 0) + 1; return acc; }, {});
    const overallTop8 = top8rows.map(s => {
      const flags = [
        assocTeams.includes(s.teamUid) && 'Affiliated',
        placeCounts[s.overallPlace] > 1 && 'Tied',
      ].filter(Boolean).join(', ');
      return {
        place: s.overallPlace,
        teamNumber: s.team?.teamNumber,
        teamName: s.team?.teamName,
        category: (categoriesMap || {})[s.team?.category] || '',
        score: s.finalScore,
        flags,
      };
    });

    // Prize Winners: Top School + Top Non Affiliated (best non-school, non-affiliated team)
    const isSchool = (uid) => ((categoriesMap || {})[uid] || '').toLowerCase().includes('school');
    const schoolUid = catUids.find(isSchool);
    const nonSchoolUids = catUids.filter(uid => !isSchool(uid));

    const bestInCat = (catFilter) => scores
      .filter(s => catFilter(s.team?.category) && Number(s.finalScore) > 0)
      .sort((a, b) => Number(a.finalScore) - Number(b.finalScore))[0] || null;

    const topSchool = schoolUid ? bestInCat(c => c === schoolUid) : null;
    const topNonAffiliated = scores
      .filter(s => nonSchoolUids.includes(s.team?.category) && !assocTeams.includes(s.teamUid) && Number(s.finalScore) > 0)
      .sort((a, b) => Number(a.finalScore) - Number(b.finalScore))[0] || null;

    const winners = [
      topSchool && { label: 'Top School', teamNumber: topSchool.team?.teamNumber, teamName: topSchool.team?.teamName, score: topSchool.finalScore },
      topNonAffiliated && { label: 'Top Professional', teamNumber: topNonAffiliated.team?.teamNumber, teamName: topNonAffiliated.team?.teamName, score: topNonAffiliated.finalScore },
    ].filter(Boolean);

    return (
      <PrintPage key={evUid}>
        <Typography variant="h5" gutterBottom>
          Event #{ev.event?.number}: {ev.event?.company ? `${ev.event.company} ` : ''}{ev.event?.name}
        </Typography>

        <Typography variant="subtitle1" sx={{ mt: 1 }}>Overall Top 8</Typography>
        <ResultTable
          rows={overallTop8}
          columns={[
            { key: 'place', label: 'Place' },
            { key: 'teamNumber', label: '#' },
            { key: 'teamName', label: 'Team' },
            { key: 'category', label: 'Category' },
            { key: 'score', label: 'Score' },
            { key: 'flags', label: 'Notes' },
          ]}
        />

        {winners.length > 0 && (
          <Box className="top-by-category">
            <Typography variant="subtitle2" gutterBottom>Prize Winners</Typography>
            {winners.map(({ label, teamNumber, teamName, score }) => (
              <Typography key={label} variant="body2" sx={{ mb: 0.5 }}>
                <strong>{label}:</strong> #{teamNumber} {teamName} — {score}
              </Typography>
            ))}
          </Box>
        )}
      </PrintPage>
    );
  });
}

function ForeignDivisionPage({ adjustedTeamsMap, divisionsMap, categoriesMap }) {
  const foreignUid = Object.keys(divisionsMap || {}).find(uid => ((divisionsMap || {})[uid] || '').toLowerCase() === 'foreign');
  if (!foreignUid) return null;
  const teams = Object.values(adjustedTeamsMap || {})
    .filter(t => t.division === foreignUid && Number(t.originalTotal) > 0)
    .sort((a, b) => Number(a.originalTotal) - Number(b.originalTotal));
  const cutoff = teams.length > 0 ? teams[Math.min(7, teams.length - 1)].originalTotal : 0;
  const top8 = (teams.length > 0 ? teams.filter(t => Number(t.originalTotal) <= Number(cutoff)) : []).map((t, i) => ({
    place: i + 1,
    teamNumber: t.teamNumber,
    teamName: t.teamName,
    category: (categoriesMap || {})[t.category] || '',
    score: t.originalTotal,
  }));

  return (
    <PrintPage>
      <Typography variant="h5" gutterBottom>
        Division: {(divisionsMap || {})[foreignUid]}
      </Typography>
      <Typography variant="subtitle1">Top 8</Typography>
      <ResultTable
        rows={top8}
        columns={[
          { key: 'place', label: 'Place' },
          { key: 'teamNumber', label: '#' },
          { key: 'teamName', label: 'Team' },
          { key: 'category', label: 'Category' },
          { key: 'score', label: 'Score' },
        ]}
      />
    </PrintPage>
  );
}

function DivisionPages({ adjustedTeamsMap, divisionsMap, categoriesMap }) {
  const skipNames = ['foreign', 'domestic'];
  const divUids = Object.keys(divisionsMap || {})
    .filter(uid => !skipNames.includes(((divisionsMap || {})[uid] || '').toLowerCase()))
    .sort((a, b) => ((divisionsMap || {})[a] || '').localeCompare((divisionsMap || {})[b] || ''));
  const teams = Object.values(adjustedTeamsMap || {});

  return divUids.map((divUid) => {
    const filtered = teams
      .filter(t => t.division === divUid && Number(t.originalTotal) > 0)
      .sort((a, b) => Number(a.originalTotal) - Number(b.originalTotal));
    // tie-safe: include all teams tied at position 8
    const top8teams = (() => {
      if (filtered.length === 0) return [];
      const cutoff = filtered[Math.min(7, filtered.length - 1)].originalTotal;
      return filtered.filter(t => Number(t.originalTotal) <= Number(cutoff));
    })();
    const top8 = top8teams.map((t, i) => ({
      place: i + 1,
      teamNumber: t.teamNumber,
      teamName: t.teamName,
      category: (categoriesMap || {})[t.category] || '',
      score: t.originalTotal,
    }));

    return (
      <PrintPage key={divUid}>
        <Typography variant="h5" gutterBottom>
          Division: {(divisionsMap || {})[divUid]}
        </Typography>
        <Typography variant="subtitle1">Top 8</Typography>
        <ResultTable
          rows={top8}
          columns={[
            { key: 'place', label: 'Place' },
            { key: 'teamNumber', label: '#' },
            { key: 'teamName', label: 'Team' },
            { key: 'category', label: 'Category' },
            { key: 'score', label: 'Score' },
          ]}
        />
      </PrintPage>
    );
  });
}

function CategoryPages({ adjustedTeamsMap, categoriesMap }) {
  const catUids = orderedCategoryUids(categoriesMap || {});
  const teams = Object.values(adjustedTeamsMap || {});

  return catUids.map((catUid) => {
    const filtered = teams
      .filter(t => t.category === catUid && Number(t.originalTotal) > 0)
      .sort((a, b) => Number(a.originalTotal) - Number(b.originalTotal));
    // tie-safe: include all teams tied at position 8
    const top8teams = (() => {
      if (filtered.length === 0) return [];
      const cutoff = filtered[Math.min(7, filtered.length - 1)].originalTotal;
      return filtered.filter(t => Number(t.originalTotal) <= Number(cutoff));
    })();
    const top8 = top8teams.map((t, i) => ({
      place: i + 1,
      teamNumber: t.teamNumber,
      teamName: t.teamName,
      score: t.originalTotal,
    }));

    return (
      <PrintPage key={catUid}>
        <Typography variant="h5" gutterBottom>
          Category: {(categoriesMap || {})[catUid]}
        </Typography>
        <Typography variant="subtitle1">Top 8</Typography>
        <ResultTable
          rows={top8}
          columns={[
            { key: 'place', label: 'Place' },
            { key: 'teamNumber', label: '#' },
            { key: 'teamName', label: 'Team' },
            { key: 'score', label: 'Score' },
          ]}
        />
      </PrintPage>
    );
  });
}

function OverallPage({ adjustedTeamsMap, categoriesMap, divisionsMap }) {
  const top8 = topN(Object.values(adjustedTeamsMap || {}), 'overallPlace', 8).map(t => ({
    place: t.overallPlace,
    teamNumber: t.teamNumber,
    teamName: t.teamName,
    category: (categoriesMap || {})[t.category] || '',
    division: (divisionsMap || {})[t.division] || '',
    score: t.originalTotal,
  }));

  return (
    <PrintPage>
      <Typography variant="h5" gutterBottom>Overall Top 8</Typography>
      <ResultTable
        rows={top8}
        columns={[
          { key: 'place', label: 'Place' },
          { key: 'teamNumber', label: '#' },
          { key: 'teamName', label: 'Team' },
          { key: 'category', label: 'Category' },
          { key: 'division', label: 'Division' },
          { key: 'score', label: 'Score' },
        ]}
      />
    </PrintPage>
  );
}
